import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  heroService,
  HeroSlide,
  getLocalizedString,
} from "../../services/firebaseService";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Helper function to parse HTML link and extract href and text
const parseHTMLLink = (htmlString: string): { href: string; text: string; isExternal: boolean } | null => {
  if (!htmlString || typeof htmlString !== 'string') {
    return null;
  }
  
  // Check if the string contains HTML anchor tag
  // This regex handles various quote styles and whitespace
  const anchorMatch = htmlString.match(/<a\s+[^>]*href\s*=\s*["']([^"']+)["'][^>]*>(.*?)<\/a>/is);
  
  if (anchorMatch) {
    const href = anchorMatch[1].trim();
    let text = anchorMatch[2].trim();
    
    // Strip any remaining HTML tags from the text
    text = text.replace(/<[^>]*>/g, '').trim();
    
    // Decode common HTML entities
    text = text
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, ' ');
    
    // Check if it's an external link (starts with http:// or https://)
    const isExternal = /^https?:\/\//i.test(href);
    return { href, text: text || href, isExternal };
  }
  
  // If no HTML found, return null to use default behavior
  return null;
};

export default function Hero() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const currentLang = i18n.language || "en";
  const [currentSlide, setCurrentSlide] = useState(0);
  const [heroContent, setHeroContent] = useState<HeroSlide[]>([]);
  const [loading, setLoading] = useState(true);

  // Load hero slides from Firebase
  useEffect(() => {
    const loadHeroSlides = async () => {
      try {
        setLoading(true);
        const slides = await heroService.getActiveHeroSlides();
        setHeroContent(slides);
      } catch (error) {
        console.error("Error loading hero slides:", error);
      } finally {
        setLoading(false);
      }
    };

    loadHeroSlides();
  }, []);

  // Auto-play slideshow
  useEffect(() => {
    if (heroContent.length === 0) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroContent.length);
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [heroContent.length]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroContent.length);
  };

  const prevSlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + heroContent.length) % heroContent.length
    );
  };

  if (loading) {
    return (
      <section className="bg-gradient-to-r from-primary via-primary-light to-accent py-24 px-8 mb-16 relative overflow-hidden min-h-[500px]">
        <div className="flex justify-center items-center min-h-[500px]">
          <div className="text-center">
            <div className="border-4 border-white/30 border-t-white rounded-full w-10 h-10 animate-spin mx-auto"></div>
          </div>
        </div>
      </section>
    );
  }

  if (heroContent.length === 0) {
    return null; // Don't render hero if no slides
  }

  return (
    <section className="bg-gradient-to-r from-primary via-primary-light to-accent py-16 px-4 sm:px-6 md:px-8 md:py-24 mb-16 relative overflow-hidden max-w-full ">
      <div className="flex justify-center items-center min-h-[500px]">
        {heroContent.map((content, index) => (
          <div
            key={content.id || index}
            className={`${
              index === currentSlide
                ? "relative opacity-100 visible"
                : "absolute top-0 left-0 w-full opacity-0 invisible"
            } transition-all duration-[800ms] ease-in-out`}
          >
            <div className="max-w-6xl mx-auto w-full grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-10 md:gap-16 items-center relative z-10 overflow-hidden">
              {/* TEXT */}
              <div className="text-white animate-[slideInLeft_0.8s_ease-out] text-center md:text-left px-2">
                <h1 className=" py-5 text-3xl sm:text-4xl md:text-6xl font-extrabold leading-tight mb-4 sm:mb-6 bg-gradient-to-br from-white to-white/80 bg-clip-text text-transparent break-words">
                  {getLocalizedString(content.title, currentLang)}
                </h1>

                <p className="text-lg sm:text-xl md:text-2xl font-semibold mb-3 sm:mb-4 text-white/95 break-words">
                  {getLocalizedString(content.subtitle, currentLang)}
                </p>

                {content.description && (
                  <p className="text-base sm:text-lg leading-relaxed mb-6 sm:mb-8 text-white/90 break-words">
                    {getLocalizedString(content.description, currentLang)}
                  </p>
                )}

                <div className="flex gap-3 flex-wrap justify-center md:justify-start">
                  {(() => {
                    const productsCTA = getLocalizedString(content.cta.products, currentLang);
                    const productsLink = parseHTMLLink(productsCTA);
                    
                    if (productsLink) {
                      const buttonClass = "bg-white text-[#2b6bee] px-6 py-3 sm:px-8 sm:py-4 rounded-lg text-base sm:text-lg font-semibold transition-all duration-200 hover:bg-white/95 hover:-translate-y-0.5 hover:shadow-lg";
                      
                      if (productsLink.isExternal) {
                        return (
                          <a
                            href={productsLink.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={buttonClass}
                          >
                            {productsLink.text || productsCTA}
                          </a>
                        );
                      } else {
                        return (
                          <Link
                            to={productsLink.href}
                            className={buttonClass}
                          >
                            {productsLink.text || productsCTA}
                          </Link>
                        );
                      }
                    }
                    
                    // Fallback to default behavior if no HTML link found
                    return (
                      <button
                        className="bg-white text-[#2b6bee] px-6 py-3 sm:px-8 sm:py-4 rounded-lg text-base sm:text-lg font-semibold transition-all duration-200 hover:bg-white/95 hover:-translate-y-0.5 hover:shadow-lg"
                        onClick={() => navigate("/products")}
                      >
                        {productsCTA}
                      </button>
                    );
                  })()}

                  {(() => {
                    const contactCTA = getLocalizedString(content.cta.contact, currentLang);
                    const contactLink = parseHTMLLink(contactCTA);
                    
                    if (contactLink) {
                      const buttonClass = "bg-white/20 text-white border border-white px-6 py-3 sm:px-8 sm:py-4 rounded-lg text-base sm:text-lg font-semibold transition-all duration-200 hover:bg-white/10 hover:-translate-y-0.5";
                      
                      if (contactLink.isExternal) {
                        return (
                          <a
                            href={contactLink.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={buttonClass}
                          >
                            {contactLink.text || contactCTA}
                          </a>
                        );
                      } else {
                        return (
                          <Link
                            to={contactLink.href}
                            className={buttonClass}
                          >
                            {contactLink.text || contactCTA}
                          </Link>
                        );
                      }
                    }
                    
                    // Fallback to default behavior if no HTML link found
                    return (
                      <button
                        className="bg-white/20 text-white border border-white px-6 py-3 sm:px-8 sm:py-4 rounded-lg text-base sm:text-lg font-semibold transition-all duration-200 hover:bg-white/10 hover:-translate-y-0.5"
                        onClick={() => navigate("/contact")}
                      >
                        {contactCTA}
                      </button>
                    );
                  })()}
                </div>
              </div>

              {/* IMAGE */}
              <div className="animate-[slideInRight_0.8s_ease-out] flex justify-center w-full overflow-hidden">
                <img
                  src={content.image}
                  alt={getLocalizedString(content.title, currentLang)}
                  className="w-full max-w-full h-auto max-h-[260px] sm:max-h-[320px] md:max-h-[400px] object-contain bg-transparent"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* FIXED: navigation buttons now INSIDE the container */}
      <button
        className="absolute top-1/2 -translate-y-1/2 left-3 text-white cursor-pointer flex items-center justify-center z-10 bg-black/25 hover:bg-black/35 p-2 rounded-full transition"
        onClick={prevSlide}
        aria-label="Previous slide"
      >
        <ChevronLeft size={28} className="md:size-[40px]" />
      </button>

      <button
        className="absolute top-1/2 -translate-y-1/2 right-3 text-white cursor-pointer flex items-center justify-center z-10 bg-black/25 hover:bg-black/35 p-2 rounded-full transition"
        onClick={nextSlide}
        aria-label="Next slide"
      >
        <ChevronRight size={28} className="md:size-[40px]" />
      </button>

      {/* Indicators */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 sm:gap-3 z-10">
        {heroContent.map((_, index) => (
          <button
            key={index}
            className={`${
              index === currentSlide
                ? "w-6 sm:w-8 h-2 bg-white rounded-md"
                : "w-3 h-3 border-2 border-white/50 bg-transparent rounded-full hover:bg-white/50"
            } transition-all duration-300`}
            onClick={() => goToSlide(index)}
          />
        ))}
      </div>

      <style>{`
    @keyframes slideInLeft {
      from { opacity: 0; transform: translateX(-30px); }
      to { opacity: 1; transform: translateX(0); }
    }
    @keyframes slideInRight {
      from { opacity: 0; transform: translateX(30px); }
      to { opacity: 1; transform: translateX(0); }
    }
  `}</style>
    </section>
  );
}
